import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NoticeSliderAPI from '../components/layout/NoticeSliderAPI';
import CasinoProviderGrid from '../components/common/CasinoProviderGrid';
import gamesService from '../services/games';
import vendorNameService from '../services/vendorNameService';
import './CasinoPage.css';

/**
 * 루미나 화면 층 신작 — 블랙 `pages/CasinoPage.jsx` 의 로직 그대로, JSX·CSS 만 교체(시안 블록 10 `cs-`/`sl-` 카지노).
 * WHY: SPEC §2 — 게임사 목록(카지노, 노출·선택 게이트)과 벤더 한글 이름은 원본 훅이 그대로 담당하고,
 *      카드 클릭 → 로비 팝업 → "로비 입장" = 원래 로비 실행은 CasinoProviderGrid 안에 있다(운영자 지시, SPEC §9-4).
 *      화면은 제목 블록(게임사 검색 + 홈으로) → 공지 슬라이더 → 게임사 5열 카드 순. 검색은 이미 받은 목록을 이름·코드로 거르는 화면 층 필터다(API 호출 없음).
 *
 * 동작 정책(블랙 원문):
 * - 게임 목록 팝업(썸네일 그리드)은 사용하지 않음. 카지노 = 로비 입장.
 */
const CasinoPage = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendorNames, setVendorNames] = useState({});
  // [루미나] 시안 "게임사 검색..." — 받은 목록을 화면에서 거르기만 한다(로직 층 무접촉)
  const [keyword, setKeyword] = useState('');

  // 초기 데이터 로드
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      // 벤더 한글 이름 매핑 (실패 시 빈 객체로 폴백 → 그리드에서 원문 표시)
      try {
        const vendorNamesData = await vendorNameService.getVendorNames();
        setVendorNames(vendorNamesData || {});
      } catch (vendorError) {
        console.warn('Failed to load vendor names, using fallback:', vendorError);
        setVendorNames({});
      }

      // 카지노 전용 제공사 목록 (활성 API 기준)
      const providersData = await gamesService.getProviders('casino');
      setProviders(providersData.providers || []);
    } catch (error) {
      console.error('데이터 로드 실패:', error);
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  /* 검색어로 카드 거르기 — 한글 이름(응답·벤더 이름 매핑)·영문 이름·코드 부분 일치 */
  const kw = keyword.trim().toLowerCase();
  const shownProviders = kw
    ? providers.filter((p) => {
        const code = (p.provider || p.provider_name || p.name || '').toString();
        const candidates = [p.name_ko, p.name, p.provider_name, code, vendorNames && vendorNames[code.toLowerCase()]];
        return candidates.some((v) => v && String(v).toLowerCase().includes(kw));
      })
    : providers;

  if (loading) {
    return (
      <section className="page page-app page-casino">
        <div className="container"><div className="lk-empty">로딩 중...</div></div>
      </section>
    );
  }

  return (
    <section className="page page-app page-casino">
      <div className="container">
        {/* 제목 블록 — 게임사 검색 + 홈으로 */}
        <div className="pg-head">
          <div><span className="eyebrow">CASINO</span><h2>카지노</h2></div>
          <div className="acts">
            <div className="sl-tools">
              <label className="sl-search">
                <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
                <input
                  type="search"
                  placeholder="게임사 검색..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  autoComplete="off"
                  aria-label="게임사 검색"
                />
              </label>
              <Link className="btn-secondary act" to="/">홈으로</Link>
            </div>
          </div>
        </div>

        {/* 공지사항 슬라이더 */}
        <NoticeSliderAPI />

        <div className="sl-meta">
          <span>게임사 <b>{shownProviders.length}</b>곳</span>
          {kw && <span>"{keyword.trim()}" 검색 결과</span>}
        </div>

        {/* 게임사 그리드 — 카드 클릭 시 로비 팝업 → "로비 입장" = 게임사 로비 실행 */}
        <CasinoProviderGrid
          providers={shownProviders}
          vendorNames={vendorNames}
        />
        {kw && shownProviders.length === 0 && <div className="lk-empty">검색 결과가 없습니다</div>}
      </div>
    </section>
  );
};

export default CasinoPage;
